import { captureException } from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { find, get } from 'lodash';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useApExams } from 'store/apexams/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import { useExams } from 'store/exams/selectors';
import { useOrgTimezone } from 'store/organizations/selectors';
import { useResources } from 'store/resources/selectors';
import { useStandardExams } from 'store/standardexams';
import { useStory } from 'store/story/selectors';
import { isVideo } from 'utils/lib/ebookHelper';
import { isAfterTime, isBeforTime } from 'utils/methods/math';

const useIsExamActive = (data) => {
  const { bookId } = typeof data === 'object' ? data : {};
  const classActivities = useSelector((state) => state.classActivities, shallowEqual);
  const classQuizzes = useSelector((state) => state.classQuizzes, shallowEqual);
  const examBook = useExams();
  const orgTimezone = useOrgTimezone();
  const showAlert = useAlert();
  const apExamBook = useApExams();
  const standardExams = useStandardExams();
  const bookUnitList = useBookUnits(); // non-assignable-books
  const bookVideos = useResources(); // for podcast and balcon
  const story = useStory();


  const checkApExamBookType = useMemo(() => {
    try {
      if (!bookId) return false;
      const exists = apExamBook.some((item) =>
        item.exam_config.some((config) => config.chapter === bookId),
      );
      return exists;
    } catch (error) {
      console.log(error);
    }
  }, [apExamBook, bookId]);


  const checkExamBookType = useMemo(() => {
    try {
      if (!bookId) return false;
      if (examBook?.length > 0) {
        for (const level of examBook) {
          for (const exam of level.exams) {
            if (Number(exam.bookId) === Number(bookId)) {
              return true;
            }
          }
        }
      }
    } catch (error) {
      captureException(`292 Error in checkExamBookType: ${error}`);
      console.log(error);
    }

    return false;
  }, [examBook, bookId]);

  const checkStandardExamBookType = useMemo(() => {
    try {
      if (!bookId) return false;
      if (standardExams?.length > 0) {
        for (const standard of standardExams) {
          if (standard.standard_exams?.length > 0) {
            for (const exam of standard.standard_exams) {
              if (Number(exam.bookId) === Number(bookId)) {
                return true;
              }
            }
          }
        }
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [standardExams, bookId]);

  const examBookIds = useMemo(() => {
    if (examBook) {
      const result = examBook?.flatMap((entry) => entry.exams.map((exam) => Number(exam.bookId)));
      return result;
    } else {
      return [];
    }
  }, [examBook]);

  const isExamActive = (chapter) => {
    try {
      const activitiy = find(classActivities, (k) => get(k, 'data.bookId') == chapter);
      const quiz = find(classQuizzes, (k) => get(k, 'data.bookId') == chapter);
      if (!activitiy && !quiz) {
        showAlert('Exam Book: Not Assigned Any assignment');
        return false;
      } else {
        const currentExamBook = find(examBook, (k) => get(k, 'bookId') == chapter);
        const startDate = get(currentExamBook, 'startDate');
        const endDate = get(currentExamBook, 'endDate');
        if (startDate && endDate) {
          if (isAfterTime(startDate, moment(), orgTimezone)) {
            showAlert(` Available by ${moment(startDate).format('MM/DD/YYYY hh:mm A')}`);
            return false;
          } else if (isBeforTime(endDate, moment(), orgTimezone)) {
            showAlert(` Exam is ended on ${moment(endDate).format('MM/DD/YYYY hh:mm A')}`);
            return false;
          } else {
            return true;
          }
        } else if (startDate) {
          if (isAfterTime(startDate, moment(), orgTimezone)) {
            showAlert(` Available by ${moment(startDate).format('MM/DD/YYYY hh:mm A')}`);
            return false;
          } else {
            return true;
          }
        } else if (endDate) {
          if (isBeforTime(endDate, moment(), orgTimezone)) {
            showAlert(` Exam is ended on ${moment(endDate).format('MM/DD/YYYY hh:mm A')}`);
            return false;
          } else {
            return true;
          }
        } else {
          return true;
        }
      }
    } catch (error) {
      captureException(error?.message || 'Error in isExamActive');
    }
  };

  const checkNonAssignableBook = (bookId) => {
    const nonAssignableBooks = bookUnitList?.some((book) => Number(book?.bookId) === Number(bookId));
    const checkStory = story?.some((book) => book?.bookId == bookId);
    const isVideoBook = isVideo(bookId, bookVideos);
    const checkNonAssignableBook = nonAssignableBooks || checkStory || isVideoBook;
    return checkNonAssignableBook ? true : false;
  }

  const checkAllTypeExamBook = (bookId) => {
    try {
      if (!bookId) return false;
      if (examBook?.length > 0) {
        for (const level of examBook) {
          for (const exam of level.exams) {
            if (Number(exam.bookId) === Number(bookId)) {
              return true;
            }
          }
        }
      }
      if (standardExams?.length > 0) {
        for (const standard of standardExams) {
          if (standard.standard_exams?.length > 0) {
            for (const exam of standard.standard_exams) {
              if (Number(exam.bookId) === Number(bookId)) {
                return true;
              }
            }
          }
        }
      }
      if (apExamBook?.length > 0) {
        const exists = apExamBook.some((item) =>
          item.exam_config.some((config) => Number(config.chapter) === Number(bookId)),
        );
        if (exists) {
          return true;
        }else{
          return false;
        }
      }

      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  return {
    isExamActive,
    examBookIds,
    checkApExamBookType,
    checkExamBookType,
    checkStandardExamBookType,
    checkAllTypeExamBook,
    checkNonAssignableBook
  };
};

export default useIsExamActive;
